import React, {memo, useCallback} from 'react';
import {InventoryReportViewVO} from '../../../types/model/valueObject/InventoryReportViewVO';
import SupplierCurrentInventoryCardActionsMenu from './actions/SupplierCurrentInventoryCardActionsMenu';
import {reportWarnings} from '../../../components/constants/report-verbiage';
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardHeaderItem,
  CollapsableCardHeaderItemWithSecondaryLabel,
  CollapsableCardBodyItem,
  CollapsableCardInfoBanner,
  CollapsableCardContainerWarning,
  CollapsableCardHeaderItemWithWarning,
  CollapsableCardHeaderItemButton
} from "../../cardComponents";
import {AssetTypeLabel} from "../../constants/securspace-constants";
import {ModalType, modalTypes} from './data';
import type { Account } from '../../../types/Account';
import { formatDurationInDHM } from '../../../util/DateUtils';

export type SupplierCurrentInventoryCardListItemToggleHandler = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => void;

type SupplierCurrentInventoryCardListItemProps = {
  index: number;
  item: InventoryReportViewVO;
  expanded?: boolean;
  onToggleExpand: SupplierCurrentInventoryCardListItemToggleHandler;
  setOpenModal: (modal: ModalType) => void;
  setAssetBeingActioned: (asset: InventoryReportViewVO) => void;
  account: Pick<Account, 'userType'>;
}

const SupplierCurrentInventoryCardListItemComponent = ({
  index,
  item,
  expanded,
  onToggleExpand,
  setOpenModal,
  setAssetBeingActioned,
  account
}: SupplierCurrentInventoryCardListItemProps) => {

  const handleToggleExpand = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    onToggleExpand(event, index);
  }, [index, onToggleExpand]);
  const handleCheckOutAsset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAssetBeingActioned(item)
    setOpenModal({type: modalTypes.CHECKOUT, open: true});
  };

  let timeInYard = '-';

  if (item.checkInDate) {
    timeInYard = formatDurationInDHM(item.checkInDate)
  }

  return <CollapsableCardContainerWarning
    expanded={expanded}
    warningLevel={item?.hasFailedPayment ? 'warning' : 'none'}
  >
    <CollapsableCardClickableContainer onClick={handleToggleExpand}>
      <CollapsableCardInfoBanner message={item?.hasFailedPayment ? reportWarnings.failedPaymentMessage : ''} />
      <CollapsableCardHeaderContainer style={{alignItems: 'flex-start'}}>
        <CollapsableCardHeaderItemWithSecondaryLabel label={'CHECK IN'} value={item?.checkInDate} secondaryLabel={'TIME IN YARD: '} secondaryValue={timeInYard} />
        <CollapsableCardHeaderItemWithWarning
          label={'CONTAINER'}
          value={item?.containerNumber}
          showWarning={!!(item?.containerNumber && !item?.containerNumberValid)}
          warningMessage={reportWarnings.containerNumberIsoWarning}
        />
        <CollapsableCardHeaderItem label={'TRAILER'} value={item?.trailerNumber} />
        <CollapsableCardHeaderItem label={'LOCATION'} value={item?.locationName} />
        <CollapsableCardHeaderItem label={'BOOKING'} value={item?.orderNumber} />
        <CollapsableCardHeaderItemButton label="Check Out" onClick={handleCheckOutAsset} />
        <CollapsableCardHeaderActionsContainer>
          <CollapsableCardExpandIconButton expanded={expanded ?? false} />
        </CollapsableCardHeaderActionsContainer>
      </CollapsableCardHeaderContainer>
    </CollapsableCardClickableContainer>
    <CollapsableCardCollapseContainer in={expanded} mountOnEnter unmountOnExit={false}>
      <CollapsableCardBodyContainer>
        <CollapsableCardHeaderItem label={'CHASSIS'} value={item?.chassisNumber} />
        <CollapsableCardBodyItem label={'CHASSIS LICENSE PLATE'} value={item?.chassisLicensePlateNumber} />
        <CollapsableCardBodyItem label={'SIZE'} value={item?.assetSize} />
        <CollapsableCardBodyItem label={'SEAL'} value={item?.sealNumber} />
        <CollapsableCardBodyItem label={'TYPE'} value={item?.assetType ? AssetTypeLabel[item.assetType] : item.assetType} />
        <CollapsableCardBodyItem label={'BUYER'} value={item?.buyerName} />
        <CollapsableCardBodyItem label={'DRIVER'} value={item?.driverFirstName + ' ' + item?.driverLastName} />
        <CollapsableCardBodyItem label={'LICENSE'} value={item?.driverLicenseNumber} />
        <CollapsableCardBodyItem label={'TRUCK LICENSE PLATE NUMBER'} value={item?.truckLicensePlateNumber} />
        <CollapsableCardBodyItem label={'SCAC CODE'} value={item?.scacCode} />
        <CollapsableCardBodyItem label={'NOTES'} value={item?.notes} />
        <SupplierCurrentInventoryCardActionsMenu setOpenModal={setOpenModal} asset={item} setAssetBeingActioned={setAssetBeingActioned} account={account}/>
      </CollapsableCardBodyContainer>
    </CollapsableCardCollapseContainer>
  </CollapsableCardContainerWarning>
};

const SupplierCurrentInventoryCardListItem = memo(SupplierCurrentInventoryCardListItemComponent);

SupplierCurrentInventoryCardListItem.displayName = 'SupplierCurrentInventoryCardListItem';

export { SupplierCurrentInventoryCardListItem };
